/**
 * Ready-to-send WhatsApp messages in English, romanised Hindi and Hindi
 * (2026-09-14; Devanagari added 2026-09-24).
 *
 * Each template is a list of lines with {placeholders}. A line whose
 * placeholders are ALL empty is dropped, so a message never shows
 * "Driver: ()" when no driver is assigned yet; partly-filled lines keep their
 * known parts.
 */
import { MARKET } from '@utils/market';

// The stored key stays `hinglish` so a couple who already picked it keeps
// their choice; only the label changed, because "Hinglish" read as a typo for
// "Hindi" to QA while the messages are in fact romanised Hindi (2026-09-24).
export const WHATSAPP_LANGUAGES = [
  { key: 'en', label: 'English' },
  { key: 'hinglish', label: 'Hindi (Roman)' },
  { key: 'hi', label: 'हिन्दी' },
];

export const WHATSAPP_TEMPLATES = {
  invite: {
    label: 'Wedding invitation',
    en: [
      'Hi {guestName}! 💌',
      "{couple} are getting married on {weddingDate}, and we'd love to celebrate with you.",
      'All the details and RSVP: {siteLink}',
    ],
    hinglish: [
      'Namaste {guestName} ji! 💌',
      '{couple} ki shaadi {weddingDate} ko hai. Aapka aana humare liye bahut khaas hoga.',
      'Saari details aur RSVP yahan: {siteLink}',
    ],
    hi: [
      "नमस्ते {guestName} जी! 💌",
      "{couple} का विवाह {weddingDate} को है। आपका आना हमारे लिए बहुत खास होगा।",
      "सारी जानकारी और RSVP यहाँ: {siteLink}",
    ],
  },
  rsvp_reminder: {
    label: 'RSVP reminder',
    en: [
      "Hi {guestName}, a gentle reminder to RSVP for {couple}'s wedding on {weddingDate} so we can plan food and seating.",
      'It takes a minute: {rsvpLink}',
    ],
    hinglish: [
      'Hi {guestName} ji, {couple} ki shaadi ({weddingDate}) ke liye apna RSVP bhej dijiye, taaki khaane aur baithne ka intezaam sahi ho sake.',
      'Bas ek minute lagega: {rsvpLink}',
    ],
    hi: [
      "नमस्ते {guestName} जी, {couple} के विवाह ({weddingDate}) के लिए अपना RSVP भेज दीजिए, ताकि भोजन और बैठने की व्यवस्था ठीक से हो सके।",
      "बस एक मिनट लगेगा: {rsvpLink}",
    ],
  },
  travel_details: {
    label: 'Ask for travel details',
    en: [
      "Hi {guestName}! We're arranging pickups and stays for {couple}'s wedding.",
      'Please share your arrival and departure: date, time, flight/train number, and how many people are travelling.',
    ],
    hinglish: [
      'Hi {guestName} ji! {couple} ki shaadi ke liye pickup aur rukne ka intezaam kar rahe hain.',
      'Kripya apni aane-jaane ki date, time, flight/train number aur kitne log aa rahe hain, bata dijiye.',
    ],
    hi: [
      "नमस्ते {guestName} जी! {couple} के विवाह के लिए आने-जाने और ठहरने की व्यवस्था कर रहे हैं।",
      "कृपया अपने आगमन और प्रस्थान की तारीख, समय, फ्लाइट/ट्रेन नंबर और कितने लोग आ रहे हैं, बता दीजिए।",
    ],
  },
  pickup_confirmation: {
    label: 'Pickup confirmation',
    en: [
      'Hi {guestName}, your pickup is arranged ✅',
      '📅 {travelDate} {pickupTime}',
      '📍 {station}',
      '🚗 Driver: {driverName} {driverPhone}',
      '{vehicle}',
      '🏨 Taking you to: {destination}',
      'Safe travels!',
    ],
    hinglish: [
      'Hi {guestName} ji, aapka pickup confirm hai ✅',
      '📅 {travelDate} {pickupTime}',
      '📍 {station}',
      '🚗 Driver: {driverName} {driverPhone}',
      '{vehicle}',
      '🏨 Aapko le jayenge: {destination}',
      'Safe journey!',
    ],
    hi: [
      "नमस्ते {guestName} जी, आपकी गाड़ी की व्यवस्था हो गई है ✅",
      "📅 {travelDate} {pickupTime}",
      "📍 {station}",
      "🚗 चालक: {driverName} {driverPhone}",
      "{vehicle}",
      "🏨 आपको यहाँ ले जाएँगे: {destination}",
      "यात्रा शुभ हो!",
    ],
  },
  driver_assignment: {
    label: 'Brief for the driver',
    en: [
      "Hi {driverName}, pickup for {couple}'s wedding:",
      '👤 {guestName} ({partySize}) {guestPhone}',
      '📅 {travelDate} {pickupTime}',
      '📍 {station} {carrierNumber}',
      '🏨 Drop at: {destination}',
    ],
    hinglish: [
      'Namaste {driverName} ji, {couple} ki shaadi ke liye pickup:',
      '👤 {guestName} ({partySize}) {guestPhone}',
      '📅 {travelDate} {pickupTime}',
      '📍 {station} {carrierNumber}',
      '🏨 Chhodna hai: {destination}',
    ],
    hi: [
      "नमस्ते {driverName} जी, {couple} के विवाह के लिए पिकअप:",
      "👤 {guestName} ({partySize}) {guestPhone}",
      "📅 {travelDate} {pickupTime}",
      "📍 {station} {carrierNumber}",
      "🏨 यहाँ छोड़ना है: {destination}",
    ],
  },
  room_details: {
    label: 'Room details',
    en: [
      "Hi {guestName}! Your stay for {couple}'s wedding:",
      '🏨 {hotel}, room {room}',
      '📅 Check-in {checkIn}, check-out {checkOut}',
      'See you there!',
    ],
    hinglish: [
      'Hi {guestName} ji! {couple} ki shaadi ke liye aapke rukne ki vyavastha:',
      '🏨 {hotel}, room {room}',
      '📅 Check-in {checkIn}, check-out {checkOut}',
      'Milte hain!',
    ],
    hi: [
      "नमस्ते {guestName} जी! {couple} के विवाह के लिए आपके ठहरने की व्यवस्था:",
      "🏨 {hotel}, कमरा {room}",
      "📅 चेक-इन {checkIn}, चेक-आउट {checkOut}",
      "वहीं मिलते हैं!",
    ],
  },
  shagun_thank_you: {
    label: 'Thank-you for a gift',
    en: [
      'Dear {giverName},',
      'Thank you so much for your blessings and your lovely gift at our wedding. It means a lot to us.',
      'With love, {couple}',
    ],
    hinglish: [
      'Pyaare {giverName} ji,',
      'Humari shaadi par aapke aashirwaad aur pyaare tohfe ke liye dil se shukriya. Aapka pyaar humare liye bahut maayne rakhta hai.',
      'Dher saara pyaar, {couple}',
    ],
    hi: [
      "प्रिय {giverName} जी,",
      "हमारे विवाह में आपके आशीर्वाद और सुंदर उपहार के लिए हृदय से धन्यवाद। आपका स्नेह हमारे लिए बहुत मायने रखता है।",
      "ढेर सारा प्यार, {couple}",
    ],
  },
  payment_reminder: {
    label: 'Payment reminder',
    en: [
      'Reminder: {amount} for {vendor} ({label}) is due on {dueDate}.',
    ],
    hinglish: [
      'Yaad dilana tha: {vendor} ka {amount} ({label}) {dueDate} tak dena hai.',
    ],
    hi: [
      "याद दिलाना था: {vendor} का {amount} ({label}) {dueDate} तक देना है।",
    ],
  },
  travel_details_request: {
    label: 'Ask for travel & stay details',
    en: [
      'Hi {guestName}! We are planning pickups and rooms for {couple}\'s wedding.',
      'Please add your arrival, departure and whether you need a room here (takes a minute):',
      '{itineraryLink}',
    ],
    hinglish: [
      'Namaste {guestName} ji! {couple} ki shaadi ke liye pickup aur rooms plan kar rahe hain.',
      'Kripya yahan apni arrival, departure aur room chahiye ya nahi, bata dijiye (bas ek minute):',
      '{itineraryLink}',
    ],
    hi: [
      "नमस्ते {guestName} जी! {couple} के विवाह के लिए पिकअप और कमरों की व्यवस्था कर रहे हैं।",
      "कृपया यहाँ अपना आगमन, प्रस्थान और कमरा चाहिए या नहीं, बता दीजिए (बस एक मिनट):",
      "{itineraryLink}",
    ],
  },
  tomorrow_reminder: {
    label: "Tomorrow's functions",
    en: [
      "Hi {guestName}, see you tomorrow for {couple}'s {eventNames}! 🎉",
      "Timings, venue and your pickup are all here: {itineraryLink}",
    ],
    hinglish: [
      "Hi {guestName} ji, kal {couple} ke {eventNames} mein milte hain! 🎉",
      "Timing, venue aur aapka pickup, sab yahan hai: {itineraryLink}",
    ],
    hi: [
      "नमस्ते {guestName} जी, कल {couple} के {eventNames} में मिलते हैं! 🎉",
      "समय, स्थान और आपका पिकअप, सब यहाँ है: {itineraryLink}",
    ],
  },
  itinerary_link: {
    label: 'Personal itinerary link',
    en: [
      'Hi {guestName}, here are your wedding details: events, stay and pickup.',
      '{itineraryLink}',
    ],
    hinglish: [
      'Hi {guestName} ji, aapki shaadi ki saari details (functions, rukna aur pickup) yahan hain:',
      '{itineraryLink}',
    ],
    hi: [
      "नमस्ते {guestName} जी, आपके लिए विवाह की सारी जानकारी (कार्यक्रम, ठहरना और पिकअप) यहाँ है:",
      "{itineraryLink}",
    ],
  },
};

const PLACEHOLDER = /\{([a-zA-Z]+)\}/g;

/** Fill a template. Lines whose placeholders are all empty are dropped. */
export const fillTemplate = (key, lang = 'en', vars = {}) => {
  const template = WHATSAPP_TEMPLATES[key];
  if (!template) return '';
  const lines = template[lang] || template.en;
  const value = (name) => {
    const v = vars[name];
    return v === null || v === undefined ? '' : String(v).trim();
  };

  return lines
    .filter(line => {
      const names = [...line.matchAll(PLACEHOLDER)].map(m => m[1]);
      return names.length === 0 || names.some(n => value(n) !== '');
    })
    .map(line => line
      .replace(PLACEHOLDER, (_, n) => value(n))
      .replace(/\(\s*\)/g, '')          // "()" left by an empty placeholder
      .replace(/\s+([,.!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .trim())
    .filter(Boolean)
    .join('\n');
};

/**
 * WhatsApp link. A bare 10-digit number gets India's +91 in the India
 * region; otherwise a number needs its country code, and without a usable
 * number WhatsApp lets the sender pick the contact.
 */
export const whatsappLink = (phone, text, market = MARKET) => {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10 && market === 'india') digits = `91${digits}`;
  if (digits.length < 11) digits = '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
};
